/**
 * Photos API — same exported operation set as the Firestore version. The
 * upload path changed shape: signed PUT direct to GCS, then metadata
 * registration (the server's URL signature enforces content-type and the
 * 5 MB cap; the ≤20-per-code cap comes back as a 409).
 */
import type { AccessoryPhoto, UploadUrlResponse } from "nailor-plus-shared";
import { api } from "@/api/client";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
// HEIC/HEIF: iPhone-default container. Browsers can't render it natively, so
// uploads succeed but the <img> ends up broken. Reject at the boundary.
// File.type can be empty on iOS Safari, so we also check the extension.
const HEIC_EXT = /\.(heic|heif)$/i;
const HEIC_MIME = /^image\/(heic|heif|heic-sequence|heif-sequence)$/i;

// Returns null when ok, an error message string otherwise. Use this at every
// upload entrypoint before kicking off the mutation.
export const validatePhotoFile = (file: File): string | null => {
  if (HEIC_EXT.test(file.name) || HEIC_MIME.test(file.type)) {
    return "HEIC photos aren't supported — please export as JPEG or PNG first.";
  }
  if (!file.type.startsWith("image/") && !/\.(jpe?g|png|webp|gif|bmp|svg)$/i.test(file.name)) {
    return "Only image files are allowed.";
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return "Photo must be under 5 MB.";
  }
  return null;
};

/** Signed-PUT upload; returns the stable image_path for metadata calls. */
const uploadBytes = async (code: string, file: File): Promise<string> => {
  const { uploadUrl, imagePath, headers } = await api<UploadUrlResponse>(
    "/api/accessory-photos/upload-url",
    { body: { code, fileName: file.name, contentType: file.type || "image/jpeg" } },
  );
  const res = await fetch(uploadUrl, { method: "PUT", headers, body: file });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  return imagePath;
};

// Fetch every photo, code → array (position-ordered, fresh signed read URLs).
export const listAccessoryPhotos = async (): Promise<Record<string, AccessoryPhoto[]>> =>
  api<Record<string, AccessoryPhoto[]>>("/api/accessory-photos");

interface UploadInput {
  code: string;
  file: File;
}

// uploadedBy is no longer a parameter — the server stamps the session user.
export const uploadAccessoryPhoto = async ({ code, file }: UploadInput): Promise<void> => {
  const imagePath = await uploadBytes(code, file);
  await api<{ imagePath: string }>("/api/accessory-photos", { body: { code, imagePath } });
};

interface RemoveInput {
  code: string;
  imagePath: string; // unique per upload — used as the entry identifier
}

export const removeAccessoryPhoto = async ({ code, imagePath }: RemoveInput): Promise<void> => {
  await api<undefined>("/api/accessory-photos/remove", { body: { code, imagePath } });
};

interface MovePhotoDocInput {
  oldCode: string;
  newCode: string;
}

// Re-key every photo under oldCode to newCode (orphan re-assign admin
// surface). Storage objects stay put; merge is capped server-side.
export const moveAccessoryPhotoDoc = async ({
  oldCode,
  newCode,
}: MovePhotoDocInput): Promise<void> => {
  await api<undefined>("/api/accessory-photos/move-code", { body: { oldCode, newCode } });
};

// Purge every photo (rows + storage objects) under a code. Admin only.
export const deleteAccessoryPhotoDoc = async (code: string): Promise<void> => {
  await api<undefined>(`/api/accessory-photos/code/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
};

interface MoveSinglePhotoInput {
  oldCode: string;
  newCode: string;
  imagePath: string;
}

export const moveSinglePhoto = async ({
  oldCode,
  newCode,
  imagePath,
}: MoveSinglePhotoInput): Promise<void> => {
  await api<undefined>("/api/accessory-photos/move", { body: { oldCode, newCode, imagePath } });
};

interface ReplacePhotoInput {
  code: string;
  imagePath: string;
  file: File;
}

// Swap a photo's bytes; server preserves gallery position and caption.
export const replacePhoto = async ({ code, imagePath, file }: ReplacePhotoInput): Promise<void> => {
  const newImagePath = await uploadBytes(code, file);
  await api<undefined>("/api/accessory-photos/replace", {
    body: { code, imagePath, newImagePath },
  });
};

interface SetCaptionInput {
  code: string;
  imagePath: string;
  caption: string;
}

// Update the caption on one photo entry. Empty string clears the field.
export const setPhotoCaption = async ({
  code,
  imagePath,
  caption,
}: SetCaptionInput): Promise<void> => {
  await api<undefined>("/api/accessory-photos/caption", { body: { code, imagePath, caption } });
};
